'use client';

import React from 'react';
import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { ANIMAL_SEX } from '@/components/animals/animalSex';
import {
  getCategoryTint,
  type EbvAverageData,
  type EbvConfigGroup,
  type EbvConfigItem,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { EbvTraitHeaderLabelFromConfig } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import type { EbvTraitHeaderContext } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import EbvGroupHeader from '@/components/publicSearch/searchSection/EbvGroupHeader';
import MatingInbreedingLine from '../MatingInbreedingLine';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import { formatEbv, getBreedAverageRaw, getEbvValue } from './helpers';
import { MATING_DISPLAY_BY } from '../types';
import type {
  AnimalEbvRecord,
  MatingDisplayBy,
  MatingPairResult,
} from '../types';
import { useMatingResultsUrls } from '../matingResultsUrls';
import { BREED_AVERAGE_FALLBACK_LABEL } from './constants';
import type { MatingTableChrome } from './useMatingTableChrome';

interface MatingPredictorResultsPivotedProps {
  ebvGroups: EbvConfigGroup[];
  ebvAverage: EbvAverageData | null;
  chrome: MatingTableChrome;
  animalUrl: (animalId: number) => string | undefined;
  currentEntry: [number, MatingPairResult[]];
  displayBy: MatingDisplayBy;
  sireMap: Map<number, AnimalEbvRecord>;
  damMap: Map<number, AnimalEbvRecord>;
  currencySymbol: string;
  onInbreedingInfoClick: () => void;
  traitHeaderContext?: EbvTraitHeaderContext;
  ebvGroupingOptions?: EbvGroupingOptions;
}

/**
 * Viewports below `md`: one row per EBV trait, columns (blank trait header) · Breed/Avg* · Dam · Sire · EPA when breed averages exist (same category order as desktop).
 */
export default function MatingPredictorResultsPivoted({
  ebvGroups,
  ebvAverage,
  chrome,
  animalUrl,
  currentEntry,
  displayBy,
  sireMap,
  damMap,
  currencySymbol,
  onInbreedingInfoClick,
  traitHeaderContext,
  ebvGroupingOptions,
}: MatingPredictorResultsPivotedProps) {
  const { buildPedigreeHref } = useMatingResultsUrls();
  const [primaryId, pairs] = currentEntry;
  const primaryAnimal =
    displayBy === MATING_DISPLAY_BY.DAM
      ? damMap.get(primaryId)
      : sireMap.get(primaryId);
  const showBreedAvgCol = ebvAverage != null;
  const categoryColSpan = showBreedAvgCol ? 5 : 4;

  /** Fixed column widths for very narrow viewports: wider trait, compact value columns. */
  const renderPivotedColgroup = () => (
    <colgroup>
      <col style={{ width: showBreedAvgCol ? '50%' : '58%' }} />
      {showBreedAvgCol ? <col style={{ width: '13%' }} /> : null}
      <col style={{ width: showBreedAvgCol ? '12.33%' : '14%' }} />
      <col style={{ width: showBreedAvgCol ? '12.33%' : '14%' }} />
      <col style={{ width: showBreedAvgCol ? '12.34%' : '14%' }} />
    </colgroup>
  );

  const pivotTraitHeaderSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: chrome.pivotHeaderForeground,
    backgroundColor: chrome.paperSurface,
    verticalAlign: 'bottom',
    pr: 0.75,
    borderBottom: chrome.cellBorder,
  } as const;

  const pivotTraitBodySx = {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    color: 'text.secondary',
    fontSize: '0.75rem',
    lineHeight: 1.35,
    pr: 0.75,
    verticalAlign: 'top',
    minWidth: 0,
  } as const;

  const pivotValueHeaderSx = {
    fontWeight: 600,
    fontSize: '0.6875rem',
    lineHeight: 1.2,
    px: 0.5,
    textAlign: 'center' as const,
    verticalAlign: 'bottom',
    color: chrome.pivotHeaderForeground,
    backgroundColor: chrome.paperSurface,
    borderBottom: chrome.cellBorder,
  } as const;

  const pivotValueBodySx = {
    fontVariantNumeric: 'tabular-nums' as const,
    fontSize: '0.6875rem',
    lineHeight: 1.25,
    px: 0.5,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
  };

  if (!primaryAnimal) {
    return (
      <Box sx={{ py: 2, px: 1 }}>
        <Typography
          variant="bodymedium"
          sx={{
            color: 'text.secondary',
          }}
        >
          No prediction results to display.
        </Typography>
      </Box>
    );
  }

  const formatAnimalEbv = (
    animal: AnimalEbvRecord | null | undefined,
    ebvConfig: EbvConfigItem,
  ): string => {
    if (!animal) return '';
    const ebvNumber = parseInt(ebvConfig.field.replace(/[^\d]/g, ''), 10);
    const ebvValue = getEbvValue(animal, ebvNumber);
    return formatEbv(ebvValue, ebvConfig.format, currencySymbol);
  };

  const renderEbvBodyRows = (
    dam: AnimalEbvRecord | null | undefined,
    sire: AnimalEbvRecord | null | undefined,
    result: MatingPairResult | null,
  ) =>
    ebvGroups.map((group) => {
      const tint = getCategoryTint(group.category, ebvGroupingOptions);
      return (
        <React.Fragment key={group.category}>
          <TableRow>
            <TableCell
              colSpan={categoryColSpan}
              sx={{
                backgroundColor: chrome.categoryHeaderBackground(tint),
                fontWeight: 700,
                fontSize: '0.7rem',
                py: 0.75,
                px: 1,
                borderBottom: chrome.cellBorder,
              }}
            >
              <EbvGroupHeader category={group.category} />
            </TableCell>
          </TableRow>
          {group.items.map((ebvConfig) => {
            const highlighted = ebvConfig.display === 2;
            const ebvNumber = parseInt(
              ebvConfig.field.replace(/[^\d]/g, ''),
              10,
            );
            const projected = result?.projectedEbvs.find(
              (candidate) => candidate.ebvNumber === ebvNumber,
            );
            const epaText =
              result != null
                ? formatEbv(
                    projected?.value ?? null,
                    ebvConfig.format,
                    currencySymbol,
                  )
                : '';
            // Index rows mirror the grid/desktop highlight: a 3px purple
            // outline around the row (text/weight unchanged from the rest of
            // the table).
            const highlightCellSx = highlighted
              ? {
                  borderTop: chrome.ebvHighlightBorder,
                  borderBottom: chrome.ebvHighlightBorder,
                }
              : null;
            // Subtle category fill on non-index trait rows (aligns the pivoted
            // mobile layout with the grid/desktop category theming). Index rows
            // keep their purple outline instead.
            const categoryRowBg = highlighted
              ? {}
              : { backgroundColor: chrome.categoryCellBg(tint) };
            const breedAvgRaw =
              showBreedAvgCol && ebvAverage
                ? getBreedAverageRaw(ebvAverage, ebvConfig)
                : null;
            return (
              <TableRow key={ebvConfig.field}>
                <TableCell
                  sx={{
                    ...pivotTraitBodySx,
                    ...categoryRowBg,
                    ...(highlightCellSx && {
                      ...highlightCellSx,
                      borderLeft: chrome.ebvHighlightBorder,
                    }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: tint,
                        flexShrink: 0,
                      }}
                    />
                    <EbvTraitHeaderLabelFromConfig
                      label={ebvConfig.label.replace(/\^/g, ' ')}
                      config={ebvConfig}
                      traitHeaderContext={traitHeaderContext}
                      typographyProps={{
                        color: 'text.secondary',
                        variant: 'bodysmall',
                        sx: { fontSize: '0.75rem', lineHeight: 1.35 },
                      }}
                    />
                  </Box>
                </TableCell>
                {showBreedAvgCol ? (
                  <TableCell
                    align="center"
                    sx={{
                      ...pivotValueBodySx,
                      ...categoryRowBg,
                      ...(highlightCellSx ?? {}),
                    }}
                  >
                    {breedAvgRaw !== undefined &&
                    breedAvgRaw !== null &&
                    breedAvgRaw !== '' ? (
                      <BreedAverageEbvCell
                        raw={breedAvgRaw}
                        format={ebvConfig.format || '1'}
                        currencySymbol={currencySymbol}
                      />
                    ) : null}
                  </TableCell>
                ) : null}
                <TableCell
                  align="center"
                  sx={{
                    ...pivotValueBodySx,
                    ...categoryRowBg,
                    ...(highlightCellSx ?? {}),
                  }}
                >
                  {formatAnimalEbv(dam, ebvConfig)}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...pivotValueBodySx,
                    ...categoryRowBg,
                    ...(highlightCellSx ?? {}),
                  }}
                >
                  {formatAnimalEbv(sire, ebvConfig)}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...pivotValueBodySx,
                    ...categoryRowBg,
                    fontWeight: result ? 600 : 400,
                    ...(highlightCellSx && {
                      borderTop: highlightCellSx.borderTop,
                      borderBottom: highlightCellSx.borderBottom,
                      borderRight: chrome.ebvHighlightBorder,
                    }),
                  }}
                >
                  {epaText}
                </TableCell>
              </TableRow>
            );
          })}
        </React.Fragment>
      );
    });

  const renderColumnHeaderRow = () => (
    <TableRow>
      <TableCell sx={pivotTraitHeaderSx} aria-label="Trait" />
      {showBreedAvgCol && (
        <TableCell
          align="center"
          sx={{
            ...pivotValueHeaderSx,
            whiteSpace: 'normal',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            hyphens: 'none',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: 1.15,
            }}
          >
            <Box component="span" sx={{ display: 'block' }}>
              Breed
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              Avg*
            </Box>
          </Box>
        </TableCell>
      )}
      <TableCell
        align="center"
        sx={{ ...pivotValueHeaderSx, whiteSpace: 'nowrap' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <AnimalSexBadge sex={ANIMAL_SEX.FEMALE} />
          Dam
        </Box>
      </TableCell>
      <TableCell
        align="center"
        sx={{ ...pivotValueHeaderSx, whiteSpace: 'nowrap' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <AnimalSexBadge sex={ANIMAL_SEX.MALE} />
          Sire
        </Box>
      </TableCell>
      <TableCell
        align="center"
        sx={{ ...pivotValueHeaderSx, whiteSpace: 'nowrap' }}
      >
        EPA
      </TableCell>
    </TableRow>
  );

  const renderDamSireHeadingBanner = (
    dam: AnimalEbvRecord | undefined,
    sire: AnimalEbvRecord | undefined,
  ) => (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        backgroundColor: chrome.epaRowBg,
        borderBottom: chrome.cellBorder,
      }}
    >
      <Stack
        direction="row"
        sx={{
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AnimalSexBadge sex={ANIMAL_SEX.FEMALE} />
          <Typography variant="bodymedium" sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              Dam
            </Box>
            {dam ? (
              <Link
                href={animalUrl(dam.animalId)}
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  wordBreak: 'break-word',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {dam.animalName} - {dam.animalIdent}
              </Link>
            ) : null}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AnimalSexBadge sex={ANIMAL_SEX.MALE} />
          <Typography variant="bodymedium" sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              Sire
            </Box>
            {sire ? (
              <Link
                href={animalUrl(sire.animalId)}
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  wordBreak: 'break-word',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {sire.animalName} - {sire.animalIdent}
              </Link>
            ) : (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                -
              </Box>
            )}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  /** Mating cards: only the animal that changes per row (sire when grouped by dam, dam when by sire). */
  const renderMatingPairHeadingBanner = (
    dam: AnimalEbvRecord,
    sire: AnimalEbvRecord,
  ) => {
    const secondary = displayBy === MATING_DISPLAY_BY.DAM ? sire : dam;
    const label =
      displayBy === MATING_DISPLAY_BY.DAM
        ? MATING_DISPLAY_BY.SIRE
        : MATING_DISPLAY_BY.DAM;
    return (
      <Box
        sx={{
          px: 1.25,
          py: 1,
          backgroundColor: chrome.epaRowBg,
          borderBottom: chrome.cellBorder,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AnimalSexBadge
            sex={
              label === MATING_DISPLAY_BY.DAM
                ? ANIMAL_SEX.FEMALE
                : ANIMAL_SEX.MALE
            }
          />
          <Typography variant="bodymedium" sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              {label}
            </Box>
            <Link
              href={animalUrl(secondary.animalId)}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                wordBreak: 'break-word',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {secondary.animalName} - {secondary.animalIdent}
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  };

  /** Inline footer; avoid a second boxed region that reads like an extra card. */
  const renderBreedAverageFootnote = () =>
    ebvAverage ? (
      <Typography
        variant="bodymedium"
        component="p"
        sx={{
          m: 0,
          pt: 1.25,
          px: 1.25,
          pb: 1.25,
          fontSize: '0.75rem',
          fontStyle: 'italic',
          color: 'text.secondary',
          lineHeight: 1.4,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        *{ebvAverage.avgsCaption ?? BREED_AVERAGE_FALLBACK_LABEL}
      </Typography>
    ) : null;

  const primaryDam: AnimalEbvRecord | undefined =
    displayBy === MATING_DISPLAY_BY.DAM ? primaryAnimal : undefined;
  const primarySire: AnimalEbvRecord | undefined =
    displayBy === MATING_DISPLAY_BY.SIRE ? primaryAnimal : undefined;

  const resolvedPairs = pairs
    .map((result) => {
      const dam = damMap.get(result.damId);
      const sire = sireMap.get(result.sireId);
      if (!dam || !sire) return null;
      return { result, dam, sire };
    })
    .filter(
      (
        resolvedPair,
      ): resolvedPair is {
        result: MatingPairResult;
        dam: AnimalEbvRecord;
        sire: AnimalEbvRecord;
      } => resolvedPair != null,
    );

  return (
    <Stack spacing={2.5} sx={{ px: 0.5, pb: 2 }}>
      {/*
        When there are mating rows, each pair card already shows this animal as Dam or Sire
        with full EBVs + EPA; a separate "primary only" preview card would duplicate that
        (e.g. 1 preview + 2 matings looked like three cards for two sires).
      */}
      {resolvedPairs.length === 0 ? (
        <Box
          sx={{
            border: `2px solid ${chrome.primaryRowBorderColor}`,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: chrome.primaryRowBaseBg,
          }}
        >
          {renderDamSireHeadingBanner(primaryDam, primarySire)}
          <Table
            size="small"
            stickyHeader
            sx={{ tableLayout: 'fixed', width: '100%' }}
          >
            {renderPivotedColgroup()}
            <TableHead>{renderColumnHeaderRow()}</TableHead>
            <TableBody>
              {renderEbvBodyRows(primaryDam, primarySire, null)}
            </TableBody>
          </Table>
          {renderBreedAverageFootnote()}
        </Box>
      ) : null}

      {resolvedPairs.map(({ result, dam, sire }, pairIndex) => {
        const isLastPair = pairIndex === resolvedPairs.length - 1;

        return (
          <Box
            key={`${result.sireId}-${result.damId}`}
            sx={{
              border: chrome.pairGroupBorder,
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: 'background.paper',
            }}
          >
            {renderMatingPairHeadingBanner(dam, sire)}
            <Table
              size="small"
              stickyHeader
              sx={{ tableLayout: 'fixed', width: '100%' }}
            >
              {renderPivotedColgroup()}
              <TableHead>{renderColumnHeaderRow()}</TableHead>
              <TableBody>{renderEbvBodyRows(dam, sire, result)}</TableBody>
            </Table>
            {result.inbreeding && (
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderTop: chrome.cellBorder,
                  backgroundColor: chrome.paperSurface,
                }}
              >
                <MatingInbreedingLine
                  layout="stacked"
                  inbreeding={result.inbreeding}
                  pedigreeHref={buildPedigreeHref(sire.animalId, dam.animalId)}
                  onInfoClick={onInbreedingInfoClick}
                />
              </Box>
            )}
            {isLastPair ? renderBreedAverageFootnote() : null}
          </Box>
        );
      })}
    </Stack>
  );
}
